import {
  useRef,
  useState,
  useLayoutEffect,
  type RefObject,
  createRef,
  LegacyRef,
} from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames/bind';
import { BREAKPOINTS_WIDTHS } from 'consts/breakpoints';
import { findLastIndex } from 'es-toolkit/compat';
import { useWindowSize } from 'hooks/useWindowSize';
import styles from './ProgressBar.module.css';

export type ProgressBarItems = {
  label: string;
  to: string;
  active?: boolean;
  uncompleted?: boolean;
  disabled?: boolean;
};

type ProgressBarProps = {
  items: ProgressBarItems[];
  onlyActiveLabel?: boolean;
};

const cx = classNames.bind(styles);

export function ProgressBar({
  items,
  onlyActiveLabel,
}: ProgressBarProps): JSX.Element {
  const { width } = useWindowSize();
  const [breakpoints, setBreakPoints] = useState<number[]>([]);
  const [totalRefCount, setTotalRefCount] = useState(0);
  const widthRef = useRef<HTMLDivElement>(null);
  const refs = useRef<RefObject<HTMLElement>[]>(
    Array.from({ length: items.length }, () => createRef<HTMLElement>()),
  );

  // `useWindowSize` reports `undefined` off the browser, and `undefined <= n` is
  // `false` — the explicit check says the same thing without the comparison.
  const isMobile = width !== undefined && width <= BREAKPOINTS_WIDTHS.MD;

  const currentProgress = findLastIndex(items, (e) => {
    return !!e.active;
  });

  // Every `?? NaN` below stands for a measurement that is not available yet:
  // these run during the first render, before the layout effect has read any
  // `offsetWidth`, and the arithmetic already produced `NaN` there — which the
  // browser drops as an invalid `calc()`. Substituting `0` would instead place
  // the element, so the fallback has to stay `NaN`.
  function getWidth(index: number) {
    if (isMobile) {
      return (100 / items.length) * index + 100 / (items.length * 2);
    }

    const number =
      ((widthRef.current?.offsetWidth ?? NaN) - totalRefCount) / items.length;

    return index
      ? (breakpoints[index - 1] ?? NaN) + number * (index + 0.5)
      : number / 2;
  }

  function getTranslatedWidth(index: number) {
    return isMobile
      ? `translate(-50%, -5px)`
      : `translate(${(refs.current?.[index]?.current?.offsetWidth ?? NaN) / 2}px, -5px)`;
  }

  function getLeft(index: number, isLabel?: boolean) {
    if (!isLabel)
      return isMobile
        ? `${getWidth(index)}%`
        : `calc(${getWidth(index)}px - 2px)`;

    if (isMobile) {
      if (index === items.length - 1) {
        const offsetWidth =
          refs.current?.[currentProgress]?.current?.offsetWidth ?? 0;
        const offset = offsetWidth / 2 - offsetWidth / 4;

        return `calc(${getWidth(index)}% - ${offset}px)`;
      }

      return `${getWidth(index)}%`;
    }
    return `calc(${getWidth(index)}px - 2px)`;
  }

  function getFilledWidth() {
    return isMobile
      ? `${getWidth(currentProgress)}%`
      : `${getWidth(currentProgress) + (refs.current?.[currentProgress]?.current?.offsetWidth ?? NaN) / 2}px`;
  }

  function getWordWrapperWidth() {
    return isMobile ? 'translate(-50%, -38px)' : 'translate(0, -38px)';
  }

  useLayoutEffect(() => {
    let totalWidth = 0;
    const array: number[] = [];

    if (refs.current?.[0]?.current?.offsetWidth) {
      refs.current?.forEach((ref) => {
        const number = ref.current?.offsetWidth ?? NaN;

        array.push((totalWidth += number));
      });
    }

    setBreakPoints(array);
    setTotalRefCount(totalWidth);
  }, [isMobile, currentProgress]);

  return (
    <div className={cx('progress-bar-wrapper')}>
      <div className={cx('progress-bar')} ref={widthRef}>
        <div
          className={cx('fill-to-progress')}
          style={{
            width: getFilledWidth(),
          }}
        />
        {}
        {items.map((item, index) => {
          const content = (
            <>
              <div
                className={cx('bullet', {
                  currentProgress,
                  active: item.active,
                })}
                style={{
                  left: getLeft(index),
                  transform: getTranslatedWidth(index),
                }}
              />
              <div
                ref={refs.current[index] as LegacyRef<HTMLDivElement>}
                style={{
                  transform: getWordWrapperWidth(),
                  left: getLeft(index, true),
                }}
                className={cx('word-wrapper', {
                  active: item.active,
                  completed: index < currentProgress,
                  uncompleted: item.uncompleted,
                })}
              >
                {item.label.split('<br />').map((label) => (
                  <div
                    className={cx({
                      hiddenLabel: onlyActiveLabel && !item.active,
                    })}
                    key={label}
                  >
                    {onlyActiveLabel && !item.active ? '.' : label}
                  </div>
                ))}
              </div>
            </>
          );

          // A disabled step renders as a plain element rather than a styled-inert
          // `Link`, so it is neither clickable nor reachable by keyboard.
          return item.disabled ? (
            <span aria-disabled="true" key={`${item.label}-${index}`}>
              {content}
            </span>
          ) : (
            <Link to={item.to} key={`${item.label}-${index}`}>
              {content}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
